import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "@/lib/toast";

import apiClient from "@/lib/api-client";
import type {
  AddPaperResult,
  CommitRequest,
  CommitResponse,
  PreviewResponse,
  ResolveCandidate,
} from "@/lib/outline-import-types";

// Handshake key: commit sets the concept id; WritingEditor reads + clears
// it on mount to auto-run scaffold generation for users arriving from the
// outline-import flow.
export const OUTLINE_AUTOSCAFFOLD_KEY = "paperchase:outline-autoscaffold";

export type OutlineImportState =
  | "paste"
  | "parsing"
  | "preview"
  | "committing"
  | "done";

export function useOutlineImport(conceptId: string) {
  const [state, setState] = useState<OutlineImportState>("paste");
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const previewMutation = useMutation({
    mutationFn: (prose: string) =>
      apiClient
        .post(`api/concepts/${conceptId}/import/outline/preview`, {
          json: { prose },
          timeout: 120_000,
        })
        .json<PreviewResponse>(),
  });

  const commitMutation = useMutation({
    mutationFn: (body: CommitRequest) =>
      apiClient
        .post(`api/concepts/${conceptId}/import/outline/commit`, {
          json: body,
          timeout: 120_000,
        })
        .json<CommitResponse>(),
  });

  const runPreview = useCallback(
    async (prose: string) => {
      setError(null);
      setState("parsing");
      try {
        const resp = await previewMutation.mutateAsync(prose);
        setPreview(resp);
        setState("preview");
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setState("paste");
      }
    },
    [previewMutation],
  );

  const runCommit = useCallback(
    async (body: CommitRequest) => {
      setError(null);
      setState("committing");
      try {
        const resp = await commitMutation.mutateAsync(body);
        // Match the actual cache keys used by useClaims/useClaimLinks/useWritingDraft
        // (see frontend/src/hooks/useClaims.ts and useWritingDraft.ts).
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["claims", conceptId] }),
          qc.invalidateQueries({ queryKey: ["claim-links", conceptId] }),
          qc.invalidateQueries({ queryKey: ["evidence-stats", conceptId] }),
          qc.invalidateQueries({ queryKey: ["completeness-matrix", conceptId] }),
          qc.invalidateQueries({ queryKey: ["writing-draft", conceptId] }),
          // Commit inserts ClaimEvidence rows — papers carry a per-paper
          // has_evidence flag, so the list must refetch.
          qc.invalidateQueries({ queryKey: ["papers", conceptId] }),
        ]);
        try {
          sessionStorage.setItem(OUTLINE_AUTOSCAFFOLD_KEY, conceptId);
        } catch {
          // Safari private mode / storage disabled — fall back to manual scaffold.
        }
        setState("done");
        toast.success(
          resp.skipped > 0
            ? `Imported ${resp.claim_count} claims (${resp.skipped} skipped as duplicates)`
            : `Imported ${resp.claim_count} claims`,
        );
        navigate({
          to: "/concepts/$conceptId/build",
          params: { conceptId },
          search: { view: "write" as const },
        });
        return resp;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setState("preview");
        throw e;
      }
    },
    [commitMutation, qc, conceptId, navigate],
  );

  const resetToPaste = useCallback(() => {
    setPreview(null);
    setError(null);
    setState("paste");
  }, []);

  return { state, preview, error, runPreview, runCommit, resetToPaste };
}

/** On-demand S2 resolution for an unmatched citation row (user-in-control).
 *  Self-contained so EvidenceRow can call it directly without prop threading. */
export function useOutlineResolve(conceptId: string) {
  const resolveCitation = useCallback(
    (citationMarker: string, excerpt: string) =>
      apiClient
        .post(`api/concepts/${conceptId}/import/outline/resolve`, {
          json: { citation_marker: citationMarker, excerpt },
          timeout: 30_000,
        })
        .json<{ candidates: ResolveCandidate[] }>()
        .then((r) => r.candidates),
    [conceptId],
  );

  const addPaper = useCallback(
    (s2PaperId: string) =>
      apiClient
        .post(`api/concepts/${conceptId}/import/outline/add-paper`, {
          json: { s2_paper_id: s2PaperId },
          timeout: 30_000,
        })
        .json<AddPaperResult>(),
    [conceptId],
  );

  return { resolveCitation, addPaper };
}
